import React, { useState, useEffect } from "react";
import { Form, Button, Checkbox, Message, Table, Dropdown, Icon } from "semantic-ui-react";
import { toast } from "react-toastify";
import { HeaderPage } from "../../components/Admin";
import { useInvoicing, useAuth } from "../../hooks";

// Configuración → Facturación: datos del emisor y series. Hoy solo MODO PRUEBA
// (proveedor simulado, nada se envía a SUNAT). Ver docs/claude/facturacion.md.

const DOC_OPTIONS = [
  { key: "BOLETA", value: "BOLETA", text: "Boleta (serie B…)" },
  { key: "FACTURA", value: "FACTURA", text: "Factura (serie F…)" },
  { key: "NOTA", value: "NOTA", text: "Nota de venta (serie N…)" },
];
const DOC_TEXT = { BOLETA: "Boleta", FACTURA: "Factura", NOTA: "Nota de venta" };
const docText = (type) => DOC_TEXT[type] || type;

export function InvoicingAdmin() {
  const { issuer, series, getIssuer, updateIssuer, getSeries, addSeries, updateSeries } =
    useInvoicing();
  const { auth } = useAuth();
  const [form, setForm] = useState(null);
  const [lookupToken, setLookupToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [newSeries, setNewSeries] = useState({ doc_type: "BOLETA", series: "", last_number: 0 });

  useEffect(() => {
    getIssuer();
    getSeries();
  }, []);
  useEffect(() => {
    if (issuer) setForm(issuer);
  }, [issuer]);

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const onSave = async () => {
    setSaving(true);
    try {
      await updateIssuer({
        enabled: !!form.enabled,
        ruc: form.ruc,
        legal_name: form.legal_name,
        trade_name: form.trade_name,
        address: form.address,
        establishment_code: form.establishment_code,
        igv_rate: form.igv_rate,
        summary_line_text: form.summary_line_text,
      });
      // El token de la consulta de RUC/DNI solo lo cambia el Súper Usuario y
      // nunca se muestra: aquí solo se escribe uno nuevo.
      if (auth?.me?.is_superuser && lookupToken.trim()) {
        await updateIssuer({ decolecta_token: lookupToken.trim() });
        setLookupToken("");
      }
      toast.success("Datos del emisor guardados");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const onAddSeries = async () => {
    try {
      await addSeries({
        doc_type: newSeries.doc_type,
        series: newSeries.series.trim().toUpperCase(),
        last_number: Number(newSeries.last_number) || 0,
      });
      setNewSeries({ ...newSeries, series: "", last_number: 0 });
      toast.success("Serie creada");
    } catch (error) {
      toast.error(error.message);
    }
  };

  const onToggleSeries = async (row, active) => {
    try {
      await updateSeries(row.id, { active });
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <>
      <HeaderPage title="Facturación electrónica" />

      <Message warning>
        <Message.Header>MODO PRUEBA</Message.Header>
        Los comprobantes que se emitan aquí son <strong>simulados</strong>: no se envían a
        SUNAT y no tienen validez tributaria. La emisión real se habilita cuando haya un
        proveedor conectado y validado con el contador del negocio.
      </Message>

      <h3>Datos del emisor</h3>
      {form && (
        <Form style={{ maxWidth: 560 }}>
          <Form.Field>
            <Checkbox
              toggle
              label="Facturación activa (los cobros podrán emitir boleta o factura)"
              checked={!!form.enabled}
              onChange={(_, data) => setField("enabled", data.checked)}
            />
          </Form.Field>
          <Form.Input
            label="RUC"
            maxLength={11}
            placeholder="11 dígitos"
            value={form.ruc || ""}
            onChange={(_, data) => setField("ruc", data.value.replace(/\D/g, ""))}
          />
          <Form.Input
            label="Razón social (tal como figura en SUNAT)"
            value={form.legal_name || ""}
            onChange={(_, data) => setField("legal_name", data.value)}
          />
          <Form.Input
            label="Nombre comercial"
            value={form.trade_name || ""}
            onChange={(_, data) => setField("trade_name", data.value)}
          />
          <Form.Input
            label="Dirección del establecimiento"
            value={form.address || ""}
            onChange={(_, data) => setField("address", data.value)}
          />
          <Form.Group widths="equal">
            <Form.Input
              label="Código de establecimiento (0000 = domicilio fiscal)"
              maxLength={4}
              value={form.establishment_code || ""}
              onChange={(_, data) => setField("establishment_code", data.value.replace(/\D/g, ""))}
            />
            <Form.Input
              label="IGV (%)"
              type="number"
              value={form.igv_rate ?? ""}
              onChange={(_, data) => setField("igv_rate", data.value)}
            />
          </Form.Group>
          <Form.Input
            label='Texto de la línea de "Venta por consumo"'
            maxLength={80}
            placeholder="Venta por consumo"
            value={form.summary_line_text || ""}
            onChange={(_, data) => setField("summary_line_text", data.value)}
          />
          {auth?.me?.is_superuser && (
            <Form.Input
              label={"Token de consulta de RUC/DNI (Decolecta) — " + (form.lookup_configured ? "configurado" : "sin configurar")}
              type="password"
              autoComplete="new-password"
              placeholder={form.lookup_configured ? "Escribe uno nuevo para reemplazarlo" : "Pega el token de Decolecta"}
              value={lookupToken}
              onChange={(_, data) => setLookupToken(data.value)}
            />
          )}
          <Button primary loading={saving} onClick={onSave}>
            Guardar
          </Button>
        </Form>
      )}

      <h3 style={{ marginTop: 32 }}>Series y numeración</h3>
      <p style={{ color: "#777", maxWidth: 640 }}>
        El correlativo avanza solo y nunca se repite ni se devuelve, ni siquiera si se anula
        un comprobante. Una serie creada no se edita ni se borra: solo se activa o desactiva.
        Si el negocio ya emite con otro sistema, usa series distintas a las de ese sistema. La nota de venta es un documento interno: su serie N001 se crea sola la primera vez.
      </p>

      <Table celled collapsing>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Tipo</Table.HeaderCell>
            <Table.HeaderCell>Serie</Table.HeaderCell>
            <Table.HeaderCell>Último número usado</Table.HeaderCell>
            <Table.HeaderCell>Activa</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {series.length === 0 && (
            <Table.Row>
              <Table.Cell colSpan="4">Todavía no hay series. Crea una de boleta y una de factura.</Table.Cell>
            </Table.Row>
          )}
          {series.map((row) => (
            <Table.Row key={row.id}>
              <Table.Cell>{docText(row.doc_type)}</Table.Cell>
              <Table.Cell>
                <strong>{row.series}</strong>
              </Table.Cell>
              <Table.Cell>{row.last_number}</Table.Cell>
              <Table.Cell>
                <Checkbox
                  toggle
                  checked={row.active}
                  onChange={(_, data) => onToggleSeries(row, data.checked)}
                />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      <Form style={{ maxWidth: 560 }}>
        <Form.Group widths="equal">
          <Form.Field>
            <label>Tipo</label>
            <Dropdown
              selection
              options={DOC_OPTIONS}
              value={newSeries.doc_type}
              onChange={(_, data) => setNewSeries({ ...newSeries, doc_type: data.value })}
            />
          </Form.Field>
          <Form.Input
            label="Serie"
            maxLength={4}
            placeholder={{ BOLETA: "B001", FACTURA: "F001", NOTA: "N001" }[newSeries.doc_type]}
            value={newSeries.series}
            onChange={(_, data) =>
              setNewSeries({ ...newSeries, series: data.value.toUpperCase() })
            }
          />
          <Form.Input
            label="Último número ya usado"
            type="number"
            min="0"
            value={newSeries.last_number}
            onChange={(_, data) => setNewSeries({ ...newSeries, last_number: data.value })}
          />
        </Form.Group>
        <Button basic onClick={onAddSeries} disabled={newSeries.series.trim().length !== 4}>
          <Icon name="plus" /> Crear serie
        </Button>
      </Form>
    </>
  );
}
